import { PHASE } from "../../../../../common/constants.ts";
import type { OwnerExpectations, Phase } from "../../../../../common/types.ts";
import { resolveMarketRoot } from "../../util/mobileNavigation.ts";

export type DashboardAction = {
  labelKey: string;
  defaultLabel: string;
  path: string[];
  // R1-1/R1-2: after arriving at `path`, expand this sheet via the generic
  // `?m_open=<sheetKey>` intent channel. `path` stays the page; `openSheet`
  // expresses "on arrival, open this in-place sheet".
  openSheet?: string;
};

// G1+R1: the deduped subject a to-do is about. Multiple raw signals (a worker
// riskAlert + a UI phase-checklist item) can describe the same subject; the
// queue collapses them to one concept so nothing double-renders. The landing
// (page + optional sheet) is resolved from the concept here on the UI side,
// where phase-aware market routing (resolveMarketRoot) lives — the worker build
// forbids pulling UI navigation into its bundle.
export type DashboardTaskConcept =
  | "roster"
  | "rosterFull"
  | "injuries"
  | "contracts"
  | "salaryPressure"
  | "goalAdvice"
  | "inbox";

const CONCEPT_BY_KEY: Record<string, DashboardTaskConcept> = {
  rosterShort: "roster",
  rosterLegal: "roster",
  rosterFull: "rosterFull",
  injuries: "injuries",
  contracts: "contracts",
  salaryPressure: "salaryPressure",
  inbox: "inbox",
  goalAdvice: "goalAdvice",
};

export const resolveDashboardConcept = (key: string): DashboardTaskConcept | undefined =>
  CONCEPT_BY_KEY[key];

export type DashboardTaskLanding = {
  path: string[];
  openSheet?: string;
  sheet?: "inbox";
};

// R1-2/R1-3/R1-4: a concept's landing. Injuries and roster-compensation CTAs
// resolve here so the worker riskAlert, the UI checklist item, and the blocker
// main task all agree. Roster/goal-advice CTAs land on the phase-aware market
// root (re-signing period → negotiation, free agency → free agents, draft →
// draft, …) instead of a hard-coded free_agents.
export const resolveDashboardTaskLanding = (
  concept: DashboardTaskConcept,
  phase: number | undefined,
): DashboardTaskLanding => {
  switch (concept) {
    case "roster":
    case "goalAdvice":
      return {
        path: resolveMarketRoot({
          phase: (phase ?? PHASE.REGULAR_SEASON) as Phase,
        }),
      };
    case "injuries":
      return { path: ["roster"], openSheet: "injuries" };
    case "contracts":
      return { path: ["negotiation"] };
    case "salaryPressure":
      return { path: ["team_finances"] };
    case "inbox":
      return { path: [], sheet: "inbox" };
    case "rosterFull":
      return { path: ["roster"] };
  }
};

export type PhaseTask = {
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  primaryAction: DashboardAction;
  secondaryAction?: DashboardAction;
};

export type DashboardRiskAlert = {
  key: string;
  severity: "danger" | "warning" | "info";
  titleKey: string;
  defaultTitle: string;
  detailKey: string;
  defaultDetail: string;
  detailParams?: Record<string, number | string>;
  path: string[];
  // R1-2: after arriving at `path`, expand this sheet via `?m_open=<sheetKey>`.
  // The injuries alert lands on the roster tab and opens the injuries sheet.
  openSheet?: string;
  // M3-5: alerts marked with a sheet open an in-place overlay instead of
  // navigating to a page route. The inbox alert opens the home InboxSheet (the
  // retired /inbox page); path stays as an inert home fallback.
  sheet?: "inbox";
};

export type DashboardPrimaryTask = {
  kind: "blocker" | "phase";
  concept?: DashboardTaskConcept;
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  descriptionParams?: Record<string, number | string>;
  primaryAction: DashboardAction;
  secondaryAction?: DashboardAction;
};

export type DashboardDecisionFocus = {
  kind: "decision";
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  primaryAction: {
    labelKey: string;
    defaultLabel: string;
  };
};

export type DashboardHomeFocus = DashboardDecisionFocus | DashboardPrimaryTask;

export type DashboardAlertMessage = {
  read?: boolean;
};

// G1-1: one merged to-do item. Phase-checklist items, riskAlerts and the inbox
// state all normalize to this shape and dedupe by concept, so a subject that a
// worker riskAlert and a UI checklist item both raise (contracts, injuries,
// roster) renders once. `actionable === false` items (rosterFull) are inert
// status badges that sink to the bottom; `done` items are satisfied phase tasks.
export type DashboardTaskItem = {
  key: string;
  concept: DashboardTaskConcept;
  severity: "danger" | "warning" | "info";
  titleKey: string;
  defaultTitle: string;
  detailKey?: string;
  defaultDetail?: string;
  detailParams?: Record<string, number | string>;
  source: DashboardTodoRole;
  done: boolean;
  actionable: boolean;
  path: string[];
  openSheet?: string;
  sheet?: "inbox";
};

export type DashboardTaskHub = {
  // Actionable, not-yet-done items in priority order, capped for display.
  todos: DashboardTaskItem[];
  overflowCount: number;
  // Satisfied phase tasks (done), shown as a quiet progress tail.
  satisfied: DashboardTaskItem[];
  // Non-actionable info badges (rosterFull) — sunk below everything.
  badges: DashboardTaskItem[];
  primaryTask: DashboardHomeFocus | undefined;
};

export type DashboardSeasonGoalState = {
  kind: "playoffRun" | "securePlayoffs" | "chasePlayoffs" | "developRoster";
  progressPercent: number;
  riskLevel: "clear" | "watch" | "danger";
};

export type DashboardSeasonGoalInput = {
  playoffRoundsWon: number;
  rank: number;
  record: {
    lost: number;
    otl?: number;
    tied?: number;
    won: number;
  };
  rosterCount?: number;
  // N4-0: when the owner's season expectation letter exists, the goal card is
  // computed against the owner's ask instead of the pure heuristic below.
  expectation?: OwnerExpectations;
};

// G1b: each to-do carries the in-game voice that raised it instead of a work-
// ticket deadline/consequence/effort estimate. Contracts and salary come from
// the players' agents; roster and injuries from the coaching staff; anything
// else (finances, standings, owner mail) from the owner.
export type DashboardTodoRole = "owner" | "coaching" | "agent";

export type DashboardTodoSource = {
  role: DashboardTodoRole;
  sourceDefault: string;
  sourceKey: string;
};

export const MAX_DASHBOARD_TASK_HUB_BLOCKERS = 3;

const severityRank = {
  danger: 0,
  warning: 1,
  info: 2,
} satisfies Record<DashboardRiskAlert["severity"], number>;

export const getHighestPriorityRiskAlert = (riskAlerts: DashboardRiskAlert[]) => {
  let highest: DashboardRiskAlert | undefined;

  for (const alert of riskAlerts) {
    if (!highest || severityRank[alert.severity] < severityRank[highest.severity]) {
      highest = alert;
    }
  }

  return highest;
};

const STRONG_PHASES = new Set<number>([
  PHASE.EXPANSION_DRAFT,
  PHASE.FANTASY_DRAFT,
  PHASE.DRAFT_LOTTERY,
  PHASE.DRAFT,
  PHASE.RESIGN_PLAYERS,
  PHASE.FREE_AGENCY,
]);

const getStrongPhaseConcepts = (
  phase: number | undefined,
  phaseTask: PhaseTask | undefined,
): DashboardTaskConcept[] => {
  const actionRoot = phaseTask?.primaryAction.path[0];

  if ((phase === PHASE.FANTASY_DRAFT || phase === PHASE.DRAFT) && actionRoot === "draft") {
    return ["roster"];
  }
  if (phase === PHASE.FREE_AGENCY && actionRoot === "free_agents") {
    return ["roster"];
  }
  if (phase === PHASE.RESIGN_PLAYERS && actionRoot === "negotiation") {
    return ["contracts", "roster"];
  }

  return [];
};

const buildDashboardBlockerTask = (
  blocker: DashboardRiskAlert,
  phase: number | undefined,
): DashboardPrimaryTask => {
  // R1-2/R1-3: when a blocker becomes the main task, its CTA resolves through
  // the same concept landing as the queue item (injuries → roster + injuries
  // sheet, roster-short → phase-aware market root), so the ×3 injuries CTA and
  // the ×2 roster CTA land identically wherever they surface.
  const concept = resolveDashboardConcept(blocker.key);
  const landing = concept ? resolveDashboardTaskLanding(concept, phase) : undefined;

  return {
    kind: "blocker",
    ...(concept ? { concept } : {}),
    titleKey: blocker.titleKey,
    defaultTitle: blocker.defaultTitle,
    descriptionKey: blocker.detailKey,
    defaultDescription: blocker.defaultDetail,
    descriptionParams: blocker.detailParams,
    primaryAction: {
      labelKey: "leagueDashboard.resolveBlocker",
      defaultLabel: "Resolve now",
      path: landing ? landing.path : blocker.path,
      ...(landing?.openSheet ? { openSheet: landing.openSheet } : {}),
    },
  };
};

export const resolveDashboardPrimaryTask = ({
  phase,
  phaseTask,
  riskAlerts,
}: {
  phase?: number;
  phaseTask?: PhaseTask;
  riskAlerts: DashboardRiskAlert[];
}): DashboardPrimaryTask | undefined => {
  // U2-1B: the named offseason moments are irreversible/high-salience actions,
  // so they keep the focus. During ordinary phases, an illegal short roster or
  // an injured starter interrupts the normal loop and sends the player to the
  // relevant roster action.
  if (phaseTask && phase !== undefined && STRONG_PHASES.has(phase)) {
    return {
      kind: "phase",
      ...phaseTask,
    };
  }

  const immediateDanger = riskAlerts.find(
    (alert) =>
      (alert.key === "rosterShort" || alert.key === "injuries") && alert.severity === "danger",
  );
  if (immediateDanger) {
    return buildDashboardBlockerTask(immediateDanger, phase);
  }

  if (phaseTask) {
    return {
      kind: "phase",
      ...phaseTask,
    };
  }

  // rosterFull is an informational capacity badge, never a task. It must not
  // become the main focus merely because no phase task is available.
  const blocker = getHighestPriorityRiskAlert(
    riskAlerts.filter((alert) => alert.key !== "rosterFull"),
  );
  if (!blocker) {
    return undefined;
  }

  return buildDashboardBlockerTask(blocker, phase);
};

export const resolveDashboardHomeFocus = ({
  hasInboxDecision,
  phase,
  phaseTask,
  riskAlerts,
}: {
  hasInboxDecision: boolean;
  phase?: number;
  phaseTask?: PhaseTask;
  riskAlerts: DashboardRiskAlert[];
}): DashboardHomeFocus | undefined => {
  if (hasInboxDecision) {
    return {
      kind: "decision",
      titleKey: "leagueDashboard.decisionPrimaryTitle",
      defaultTitle: "Decision waiting",
      descriptionKey: "leagueDashboard.decisionPrimaryDescription",
      defaultDescription: "The owner is waiting for your response.",
      primaryAction: {
        labelKey: "leagueDashboard.decisionPrimaryAction",
        defaultLabel: "Review decision",
      },
    };
  }

  return resolveDashboardPrimaryTask({ phase, phaseTask, riskAlerts });
};

export const getDashboardFallbackPrimaryTask = (): DashboardPrimaryTask => ({
  kind: "phase",
  titleKey: "leagueDashboard.fallbackPrimaryTitle",
  defaultTitle: "Review roster readiness",
  descriptionKey: "leagueDashboard.fallbackPrimaryDescription",
  defaultDescription:
    "Nothing urgent is waiting. Check your roster's health and rotation before you move on.",
  primaryAction: {
    labelKey: "leagueDashboard.fallbackPrimaryAction",
    defaultLabel: "Review roster",
    path: ["roster"],
  },
});

export const getDashboardAlerts = ({
  messages,
  riskAlerts,
  unreadMessageCount,
}: {
  messages: DashboardAlertMessage[];
  riskAlerts: DashboardRiskAlert[];
  unreadMessageCount?: number;
}) => {
  const unreadMessages =
    unreadMessageCount ?? messages.filter((message) => !message.read).length;

  return [
    ...riskAlerts,
    ...(unreadMessages > 0
      ? [
          {
            key: "inbox",
            severity: "info" as const,
            titleKey: "leagueDashboard.riskInbox",
            defaultTitle: "Unread messages",
            detailKey: "leagueDashboard.riskInboxDetail",
            defaultDetail: "{{count}} unread message(s)",
            detailParams: { count: unreadMessages },
            // M3-5: opens the home InboxSheet overlay in place; path is an
            // inert home fallback (the /inbox route is gone).
            path: [],
            sheet: "inbox" as const,
          },
        ]
      : []),
  ];
};

const CONCEPT_ROLE: Record<DashboardTaskConcept, DashboardTodoRole> = {
  roster: "coaching",
  rosterFull: "coaching",
  injuries: "coaching",
  contracts: "agent",
  salaryPressure: "agent",
  goalAdvice: "coaching",
  inbox: "owner",
};

// Display order for concepts that tie on priority rank (a stable secondary key).
const CONCEPT_ORDER: DashboardTaskConcept[] = [
  "roster",
  "injuries",
  "contracts",
  "salaryPressure",
  "goalAdvice",
  "inbox",
  "rosterFull",
];

const PHASE_TASK_CONCEPTS = new Set<DashboardTaskConcept>(["roster", "injuries", "contracts"]);

// Priority rank per the G1-1 order: danger blocker > incomplete phase task
// (warning) > other warning > info > satisfied phase task > inert status badge.
const dashboardTaskRank = (item: DashboardTaskItem): number => {
  if (!item.actionable) {
    return 500;
  }
  if (item.done) {
    return 400;
  }
  if (item.severity === "danger") {
    return 0;
  }
  if (item.severity === "warning") {
    return PHASE_TASK_CONCEPTS.has(item.concept) ? 100 : 150;
  }
  return 200;
};

const buildInboxTaskItem = (unread: number): DashboardTaskItem => ({
  key: "inbox",
  concept: "inbox",
  severity: "info",
  titleKey: "leagueDashboard.riskInbox",
  defaultTitle: "Unread messages",
  detailKey: "leagueDashboard.riskInboxDetail",
  defaultDetail: "{{count}} unread message(s)",
  detailParams: { count: unread },
  source: "owner",
  done: false,
  actionable: true,
  // M3-5: opens the home InboxSheet overlay in place; path is an inert home
  // fallback (the /inbox route is gone).
  path: [],
  sheet: "inbox",
});

// G1-1: fold the phase checklist, the worker riskAlerts and the inbox state into
// one priority queue. Every raw signal maps to a concept and dedupes by it — a
// worker riskAlert and a UI checklist item describing the same subject collapse
// to one row (contracts / injuries / roster stop double-rendering). The main
// phase task stays the pinned card above; when a blocker IS the main task its
// concept is dropped from the queue so it is not shown twice.
export const getDashboardTaskHub = ({
  checklistItems = [],
  goalAdvice,
  hasInboxDecision = false,
  messages,
  phase,
  phaseTask,
  riskAlerts,
  unreadMessageCount,
}: {
  checklistItems?: DashboardChecklistItem[];
  goalAdvice?: DashboardTaskItem;
  hasInboxDecision?: boolean;
  messages: DashboardAlertMessage[];
  phase?: number;
  phaseTask?: PhaseTask;
  riskAlerts: DashboardRiskAlert[];
  unreadMessageCount?: number;
}): DashboardTaskHub => {
  const primaryTask = resolveDashboardHomeFocus({
    hasInboxDecision,
    phase,
    phaseTask,
    riskAlerts,
  });
  const primaryFocusConcepts = new Set<DashboardTaskConcept>(
    primaryTask?.kind === "decision"
      ? ["inbox"]
      : primaryTask?.kind === "blocker" && primaryTask.concept
        ? [primaryTask.concept]
        : primaryTask?.kind === "phase"
          ? getStrongPhaseConcepts(phase, phaseTask)
          : [],
  );

  const checklistByConcept = new Map<DashboardTaskConcept, DashboardChecklistItem>();
  for (const item of checklistItems) {
    const concept = resolveDashboardConcept(item.key);
    if (concept && !checklistByConcept.has(concept)) {
      checklistByConcept.set(concept, item);
    }
  }
  const riskByConcept = new Map<DashboardTaskConcept, DashboardRiskAlert>();
  for (const alert of riskAlerts) {
    const concept = resolveDashboardConcept(alert.key);
    if (concept) {
      const previous = riskByConcept.get(concept);
      if (!previous || severityRank[alert.severity] < severityRank[previous.severity]) {
        riskByConcept.set(concept, alert);
      }
    }
  }

  const buildConceptItem = (concept: DashboardTaskConcept): DashboardTaskItem | undefined => {
    const checklist = checklistByConcept.get(concept);
    const risk = riskByConcept.get(concept);
    if (!checklist && !risk) {
      return undefined;
    }

    const landing = resolveDashboardTaskLanding(concept, phase);
    const source = CONCEPT_ROLE[concept];

    // rosterFull is informational only — an inert status badge, never a CTA.
    if (concept === "rosterFull") {
      return {
        key: concept,
        concept,
        severity: "info",
        titleKey: risk!.titleKey,
        defaultTitle: risk!.defaultTitle,
        detailKey: risk!.detailKey,
        defaultDetail: risk!.defaultDetail,
        detailParams: risk!.detailParams,
        source,
        done: false,
        actionable: false,
        path: landing.path,
      };
    }

    // A live riskAlert means the concept is an active problem: take its
    // severity + copy. Otherwise it is a satisfied (done) checklist item.
    if (risk) {
      return {
        key: concept,
        concept,
        severity: risk.severity,
        titleKey: risk.titleKey,
        defaultTitle: risk.defaultTitle,
        detailKey: risk.detailKey,
        defaultDetail: risk.defaultDetail,
        detailParams: risk.detailParams,
        source,
        done: false,
        actionable: true,
        path: landing.path,
        ...(landing.openSheet ? { openSheet: landing.openSheet } : {}),
        ...(landing.sheet ? { sheet: landing.sheet } : {}),
      };
    }

    return {
      key: concept,
      concept,
      severity: "info",
      titleKey: checklist!.titleKey,
      defaultTitle: checklist!.defaultTitle,
      detailKey: checklist!.detailKey,
      defaultDetail: checklist!.defaultDetail,
      detailParams: checklist!.detailParams,
      source: checklist!.source,
      done: checklist!.done,
      actionable: true,
      path: landing.path,
      ...(landing.openSheet ? { openSheet: landing.openSheet } : {}),
      ...(landing.sheet ? { sheet: landing.sheet } : {}),
    };
  };

  const unread = unreadMessageCount ?? messages.filter((message) => !message.read).length;
  const items: DashboardTaskItem[] = [];
  for (const concept of CONCEPT_ORDER) {
    if (primaryFocusConcepts.has(concept)) {
      continue;
    }
    if (concept === "inbox") {
      if (unread > 0) {
        items.push(buildInboxTaskItem(unread));
      }
      continue;
    }
    if (concept === "goalAdvice") {
      if (goalAdvice) {
        items.push(goalAdvice);
      }
      continue;
    }
    const item = buildConceptItem(concept);
    if (item) {
      items.push(item);
    }
  }

  const sorted = [...items].sort((a, b) => dashboardTaskRank(a) - dashboardTaskRank(b));
  const actionableTodos = sorted.filter((item) => item.actionable && !item.done);
  const todos = actionableTodos.slice(0, MAX_DASHBOARD_TASK_HUB_BLOCKERS);

  return {
    todos,
    overflowCount: actionableTodos.length - todos.length,
    satisfied: sorted.filter((item) => item.actionable && item.done),
    badges: sorted.filter((item) => !item.actionable),
    primaryTask,
  };
};

// N4-0: goal state measured against the owner's stated expectation ("contract").
// Progress is wins vs the win target; risk is the projected full-season pace vs
// that target. In/after the playoffs the playoff run takes over, same as the
// heuristic path.
const getSeasonGoalStateFromExpectation = (
  expectation: OwnerExpectations,
  { playoffRoundsWon, record }: Pick<DashboardSeasonGoalInput, "playoffRoundsWon" | "record">,
): DashboardSeasonGoalState => {
  if (playoffRoundsWon >= 0) {
    return {
      kind: "playoffRun",
      progressPercent: Math.min(100, 35 + playoffRoundsWon * 20),
      riskLevel: "clear",
    };
  }

  const { numGames, playoffTarget, winTarget } = expectation;
  const gamesPlayed = record.won + record.lost + (record.otl ?? 0) + (record.tied ?? 0);

  const progressPercent =
    winTarget > 0 ? Math.min(100, Math.round((record.won / winTarget) * 100)) : 0;

  const kind: DashboardSeasonGoalState["kind"] =
    playoffTarget === "develop"
      ? "developRoster"
      : gamesPlayed === 0
        ? "securePlayoffs"
        : (record.won / gamesPlayed) * numGames >= winTarget
          ? "securePlayoffs"
          : "chasePlayoffs";

  let riskLevel: DashboardSeasonGoalState["riskLevel"];
  if (gamesPlayed === 0) {
    riskLevel = "clear";
  } else {
    const projectedWins = (record.won / gamesPlayed) * numGames;
    if (projectedWins >= winTarget) {
      riskLevel = "clear";
    } else if (projectedWins >= winTarget * 0.85) {
      riskLevel = "watch";
    } else {
      riskLevel = "danger";
    }
  }

  // A building year is graded gently — the owner isn't expecting wins yet.
  if (playoffTarget === "develop" && riskLevel === "danger") {
    riskLevel = "watch";
  }

  return { kind, progressPercent, riskLevel };
};

export const getDashboardSeasonGoalState = ({
  expectation,
  playoffRoundsWon,
  rank,
  record,
  rosterCount,
}: DashboardSeasonGoalInput): DashboardSeasonGoalState => {
  if (expectation) {
    return getSeasonGoalStateFromExpectation(expectation, {
      playoffRoundsWon,
      record,
    });
  }

  if (playoffRoundsWon >= 0) {
    return {
      kind: "playoffRun",
      progressPercent: Math.min(100, 35 + playoffRoundsWon * 20),
      riskLevel: "clear",
    };
  }

  if (rosterCount !== undefined && rosterCount < 10) {
    return {
      kind: "developRoster",
      progressPercent: 25,
      riskLevel: "danger",
    };
  }

  const gamesPlayed = record.won + record.lost + (record.otl ?? 0) + (record.tied ?? 0);
  if (gamesPlayed === 0) {
    return {
      kind: "developRoster",
      progressPercent: 0,
      riskLevel: "clear",
    };
  }
  const winPct = gamesPlayed > 0 ? record.won / gamesPlayed : 0;

  if (rank <= 6 || winPct >= 0.58) {
    return {
      kind: "securePlayoffs",
      progressPercent: 75,
      riskLevel: "clear",
    };
  }

  if (rank <= 10 || winPct >= 0.45) {
    return {
      kind: "chasePlayoffs",
      progressPercent: 50,
      riskLevel: "watch",
    };
  }

  return {
    kind: "developRoster",
    progressPercent: 30,
    riskLevel: "watch",
  };
};

const TODO_ROLE_BY_KEY: Record<string, DashboardTodoRole> = {
  contracts: "agent",
  salaryPressure: "agent",
  roster: "coaching",
  rosterShort: "coaching",
  rosterLegal: "coaching",
  rosterFull: "coaching",
  injuries: "coaching",
  goalAdvice: "coaching",
  inbox: "owner",
};

const TODO_ROLE_DEFAULT_LABEL: Record<DashboardTodoRole, string> = {
  owner: "From the owner",
  coaching: "From the coaching staff",
  agent: "From the agents",
};

export const getDashboardTodoSource = (alert: { key: string }): DashboardTodoSource => {
  const role = TODO_ROLE_BY_KEY[alert.key] ?? "owner";

  return {
    role,
    sourceDefault: TODO_ROLE_DEFAULT_LABEL[role],
    sourceKey: `leagueDashboard.todoSource.${role}`,
  };
};

// G1b: current-season owner trust (three-factor ownerMood) surfaced on the home
// coach-seat card. Server sends only the current value.
export type DashboardOwnerMood = {
  inGracePeriod: boolean;
  money: number;
  playoffs: number;
  total: number;
  wins: number;
};

export type DashboardOwnerTrustTier =
  | "honeymoon"
  | "excellent"
  | "good"
  | "solid"
  | "shaky"
  | "hotSeat";

export type DashboardOwnerTrustState = {
  tier: DashboardOwnerTrustTier;
  warning: boolean;
};

// Tier thresholds mirror the owner's annual evaluation (worker genMessage.ts
// getMoodScore): total >= 2 excellent, >= 1 good, >= 0 solid, > -0.5 shaky,
// else the hot seat. During the grace period the owner is still holding fire.
export const getDashboardOwnerTrustState = (
  ownerMood: Pick<DashboardOwnerMood, "inGracePeriod" | "total">,
): DashboardOwnerTrustState => {
  if (ownerMood.inGracePeriod) {
    return { tier: "honeymoon", warning: false };
  }

  const { total } = ownerMood;
  let tier: DashboardOwnerTrustTier;

  if (total >= 2) {
    tier = "excellent";
  } else if (total >= 1) {
    tier = "good";
  } else if (total >= 0) {
    tier = "solid";
  } else if (total > -0.5) {
    tier = "shaky";
  } else {
    tier = "hotSeat";
  }

  // §5 moment 8 (persistent, weakened form): dropped below the warning line and
  // out of the grace period → the card takes the warning treatment.
  return { tier, warning: tier === "hotSeat" };
};

// N4-0 ③: per-phase to-do checklist. Each item's done state is derived from real
// team state (roster size, expiring contracts, injuries) — never manually
// checked — and carries the in-game voice that raised it (owner / coaching /
// agent, same as getDashboardTodoSource). The main phase task stays the
// highlighted CTA above; this is the收敛的 secondary list.
export type DashboardChecklistItem = {
  key: string;
  titleKey: string;
  defaultTitle: string;
  detailKey?: string;
  defaultDetail?: string;
  detailParams?: Record<string, number | string>;
  done: boolean;
  source: DashboardTodoRole;
  path: string[];
  // R1-2: injuries carries the injuries-sheet intent; the queue and any
  // standalone render both append `?m_open=<openSheet>` to the roster landing.
  openSheet?: string;
};

export type DashboardPhaseChecklistInput = {
  phase: number;
  rosterCount?: number;
  minRosterSize: number;
  maxRosterSize: number;
  pendingNegotiationCount?: number;
  injuredPlayers?: number;
};

type ChecklistKind = "rosterLegal" | "contracts" | "injuries";

// Which derivable to-dos matter in each phase (order = display order, max 3).
const PHASE_CHECKLIST_KINDS: Record<number, ChecklistKind[]> = {
  [PHASE.EXPANSION_DRAFT]: ["rosterLegal"],
  [PHASE.FANTASY_DRAFT]: ["rosterLegal"],
  [PHASE.PRESEASON]: ["rosterLegal"],
  [PHASE.REGULAR_SEASON]: ["injuries", "rosterLegal"],
  [PHASE.AFTER_TRADE_DEADLINE]: ["injuries", "rosterLegal"],
  [PHASE.PLAYOFFS]: ["injuries"],
  [PHASE.DRAFT_LOTTERY]: ["rosterLegal"],
  [PHASE.DRAFT]: ["rosterLegal"],
  [PHASE.AFTER_DRAFT]: ["rosterLegal"],
  [PHASE.RESIGN_PLAYERS]: ["contracts", "rosterLegal"],
  [PHASE.FREE_AGENCY]: ["rosterLegal"],
};

const buildChecklistItem = (
  kind: ChecklistKind,
  input: DashboardPhaseChecklistInput,
): DashboardChecklistItem | undefined => {
  switch (kind) {
    case "rosterLegal": {
      const rosterCount = input.rosterCount;
      // R0-4: once the roster clears the minimum, "13/10 players signed"
      // reads like an over-quota fraction. Split the copy: the met state
      // states the count and that the floor is satisfied; the short state
      // keeps the count/min progress fraction (always count < min there).
      const done = rosterCount !== undefined && rosterCount >= input.minRosterSize;
      // R1-3: filling the roster is a get-help action — land on the
      // phase-aware market root (re-sign / free agency / draft / trade), not
      // the roster page or a hard-coded free_agents.
      const landing = resolveDashboardTaskLanding("roster", input.phase);
      return {
        key: "rosterLegal",
        titleKey: "leagueDashboard.checklist.rosterLegal",
        defaultTitle: "Fill the roster",
        detailKey: done
          ? "leagueDashboard.checklist.rosterLegalDetailDone"
          : "leagueDashboard.checklist.rosterLegalDetail",
        defaultDetail: done
          ? "{{count}} signed, minimum {{min}} met"
          : "{{count}}/{{min}} players signed",
        detailParams: {
          count: rosterCount ?? 0,
          min: input.minRosterSize,
        },
        done,
        source: "coaching",
        path: landing.path,
      };
    }
    case "contracts": {
      const pending = input.pendingNegotiationCount ?? 0;
      const landing = resolveDashboardTaskLanding("contracts", input.phase);
      return {
        key: "contracts",
        titleKey: "leagueDashboard.checklist.contracts",
        defaultTitle: "Handle expiring contracts",
        detailKey: "leagueDashboard.checklist.contractsDetail",
        defaultDetail: "{{count}} player(s) need a decision",
        detailParams: { count: pending },
        done: pending === 0,
        source: "agent",
        path: landing.path,
      };
    }
    case "injuries": {
      const injured = input.injuredPlayers ?? 0;
      // R1-2: injuries are reviewed via the roster tab's injuries sheet (the
      // standalone page was retired); land on roster and open that sheet.
      const landing = resolveDashboardTaskLanding("injuries", input.phase);
      return {
        key: "injuries",
        titleKey: "leagueDashboard.checklist.injuries",
        defaultTitle: "Check the injury report",
        detailKey: "leagueDashboard.checklist.injuriesDetail",
        defaultDetail: "{{count}} injured player(s)",
        detailParams: { count: injured },
        done: injured === 0,
        source: "coaching",
        path: landing.path,
        ...(landing.openSheet ? { openSheet: landing.openSheet } : {}),
      };
    }
  }
};

export const getDashboardPhaseChecklist = (
  input: DashboardPhaseChecklistInput,
): DashboardChecklistItem[] => {
  const kinds = PHASE_CHECKLIST_KINDS[input.phase] ?? [];

  return kinds
    .map((kind) => buildChecklistItem(kind, input))
    .filter((item): item is DashboardChecklistItem => item !== undefined);
};

// G1-2: the season-goal card becomes a driver, not a display. When the projected
// pace puts a playoff goal at watch/danger, inject at most one goal-driven
// suggestion into the queue that points at the phase-aware market root. A
// building year (developRoster) and an in-progress playoff run (playoffRun) get
// no nudge, and a clear goal gets none either — the owner only speaks up when
// the season is actually slipping. Copy is in the coaching-staff voice.
export const getDashboardGoalAdvice = (
  goalState: Pick<DashboardSeasonGoalState, "kind" | "riskLevel">,
  phase: number | undefined,
): DashboardTaskItem | undefined => {
  if (goalState.riskLevel !== "watch" && goalState.riskLevel !== "danger") {
    return undefined;
  }
  if (goalState.kind !== "chasePlayoffs" && goalState.kind !== "securePlayoffs") {
    return undefined;
  }

  const landing = resolveDashboardTaskLanding("goalAdvice", phase);
  const isChase = goalState.kind === "chasePlayoffs";

  return {
    key: "goalAdvice",
    concept: "goalAdvice",
    severity: goalState.riskLevel === "danger" ? "danger" : "warning",
    titleKey: isChase
      ? "leagueDashboard.goalAdvice.chasePlayoffsTitle"
      : "leagueDashboard.goalAdvice.securePlayoffsTitle",
    defaultTitle: isChase ? "Push for a playoff spot" : "Protect your playoff spot",
    detailKey: isChase
      ? "leagueDashboard.goalAdvice.chasePlayoffsDetail"
      : "leagueDashboard.goalAdvice.securePlayoffsDetail",
    defaultDetail: isChase
      ? "You're below the line — the staff wants you to look at reinforcements."
      : "Your seeding is slipping. See what the market can add before it costs you.",
    source: "coaching",
    done: false,
    actionable: true,
    path: landing.path,
  };
};
